import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "../api/client";
import { useSession } from "../auth/session";
import { subscribeLiveStatus } from "../live/liveStatus";
import { deltaSync, scheduleDeltaSync } from "../state/deltaSync";
import { useJobProgressStore } from "../state/jobProgressStore";

// §8/D47/D149 — koppelt den Live-Status-Push (Durable Object → WS) an den Query-Cache. Statt bei
// jedem Notice alle Listen blind zu invalidieren (Reconnect-Storm aus presign-Fanout-Fetches),
// läuft jetzt ein DELTA-SYNC (state/deltaSync): EIN Abgleich zieht nur die seit dem Cursor
// (updated_at) geänderten Zeilen in den persistierten Cache. REALTIME-ONLY (kein Polling): bei
// jedem (Re)Connect zieht onOpen mit EINEM Delta verpasste Übergänge nach. App-Shell-Singleton.
export function useLiveStatus(): void {
  const { status, token, user } = useSession();
  const qc = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (status !== "signed-in" || !token || !userId) return;
    // F-5/D47: ephemeres Channel-Token vom api-worker minten (JWT bleibt im Header).
    const unsubscribe = subscribeLiveStatus(() => api.liveToken(), {
      onOpen: () => {
        // Sync-on-(re)connect: EIN Delta seit dem Cursor (D149) — kein Blind-Invalidate, kein Poll.
        void deltaSync(qc, userId);
      },
      onNotice: (notice) => {
        // D147: ein REINER Fortschritts-Push (stepIndex/stepCount gesetzt) aktualisiert NUR den
        // Live-Balken — kein Delta nötig (der i/n-Stand ist live-only, nicht im Cache).
        if (typeof notice.stepIndex === "number" && typeof notice.stepCount === "number") {
          useJobProgressStore.getState().setProgress(notice.jobId, notice.stepIndex, notice.stepCount);
          return;
        }
        // D147: ein terminaler Status räumt den Live-Balken-Eintrag (der Job ist nicht mehr
        // 'processing'); ein laufender Resume-Tick füllt ihn beim nächsten Knoten-Push neu.
        if (notice.status === "done" || notice.status === "failed" || notice.status === "canceled") {
          useJobProgressStore.getState().clearProgress(notice.jobId);
        }
        // D149: ein Status-Notice → Delta-Sync (debounced gegen Notice-Bursts) statt 4× invalidate.
        // EIN Delta trägt den geänderten Job, neu materialisierte Output-Assets (in ihren Ordner)
        // UND das Credit-Saldo nach — und invalidiert nur das ggf. offene Job-Detail gezielt.
        scheduleDeltaSync(qc, userId);
      },
    });
    return unsubscribe;
  }, [status, token, userId, qc]);
}
